/** Positional-theta controls (docs/positional_theta.md 3.0.2 + 3.1.1).
 *
 * The operator surface for the fifth strategy: the engine's cursor and
 * dry-run state, the sizing base (funds + compounded paper P&L), the
 * editable funds/reserve fields, and the Take-entry button — direction
 * is decided by the trend (spot vs SuperTrend line), never picked here.
 * Rendered only for positional_theta strategies. */

import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ApiError, strategiesApi } from "@/lib/api";
import { thetaApi } from "@/lib/thetaApi";
import type { Strategy } from "@/types/strategy";
import { PullbackFields } from "./ThetaPullbackFields";
import { STRATEGIES_KEY } from "./useStrategyActions";

const STATUS_KEY = (id: string) => ["theta-status", id] as const;
const STATUS_POLL_MS = 30_000;

interface Props {
  strategy: Strategy;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}

export function ThetaControlsCard({ strategy, onSaved, onError }: Readonly<Props>): ReactElement {
  const status = useQuery({
    queryKey: STATUS_KEY(strategy.id),
    queryFn: () => thetaApi.status(strategy.id),
    refetchInterval: STATUS_POLL_MS,
  });
  return (
    <section className="card mb-3" data-testid="theta-controls">
      <HeaderBadges
        dryRun={status.data?.dry_run === true}
        basketOpen={status.data?.has_open_basket === true}
      />
      <div className="mb-3 grid gap-2 text-sm text-ink-secondary sm:grid-cols-3">
        <StatusItem label="Sizing base" value={rupees(status.data?.sizing_base)} />
        <StatusItem label="Last bar" value={barLabel(status.data?.last_bar_ts)} />
        <StatusItem label="Last action" value={status.data?.last_action ?? "—"} />
      </div>
      <SizingFields strategy={strategy} onSaved={onSaved} onError={onError} />
      <PullbackFields
        strategy={strategy}
        locked={status.data?.has_open_basket === true}
        onSaved={onSaved}
        onError={onError}
      />
      <TakeEntryRow
        strategy={strategy}
        disabled={status.data?.has_open_basket === true}
        onDone={onSaved}
        onError={onError}
      />
    </section>
  );
}

function HeaderBadges({
  dryRun,
  basketOpen,
}: Readonly<{ dryRun: boolean; basketOpen: boolean }>): ReactElement {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <i className="ti ti-hourglass-high text-lg text-accent" />
      <h3>Theta spreads</h3>
      {dryRun && (
        <span className="pill bg-warning-bg text-warning">
          <i className="ti ti-flask text-xs" />
          Dry-run
        </span>
      )}
      {basketOpen && (
        <span className="pill bg-accent-bg text-accent">
          <i className="ti ti-calendar-week text-xs" />
          Basket open
        </span>
      )}
    </div>
  );
}

function StatusItem({ label, value }: Readonly<{ label: string; value: string }>): ReactElement {
  return (
    <span>
      <span className="text-ink-muted">{label}:</span> {value}
    </span>
  );
}

function rupees(value: number | undefined): string {
  if (value === undefined) return "—";
  return `Rs ${Math.round(value).toLocaleString("en-IN")}`;
}

function barLabel(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function SizingFields({ strategy, onSaved, onError }: Readonly<Props>): ReactElement {
  const qc = useQueryClient();
  const [funds, setFunds] = useState(String(strategy.allocated_funds));
  const [fraction, setFraction] = useState(String(Math.round(strategy.deploy_fraction * 100)));
  const mut = useMutation({
    mutationFn: () =>
      strategiesApi.update(strategy.id, {
        allocated_funds: Number(funds),
        deploy_fraction: Number(fraction) / 100,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STRATEGIES_KEY });
      void qc.invalidateQueries({ queryKey: STATUS_KEY(strategy.id) });
      onSaved("Sizing saved");
    },
    onError: (err) => {
      const reason = err instanceof ApiError ? `HTTP ${String(err.status)}` : "network";
      onError(`Sizing save failed: ${reason}`);
    },
  });
  const valid = Number(funds) >= 10_000 && Number(fraction) > 0 && Number(fraction) <= 100;
  return (
    <div className="mb-3 flex flex-wrap items-end gap-3">
      <LabeledNumber label="Allocated funds (Rs)" value={funds} onChange={setFunds} />
      <LabeledNumber label="Deployable %" value={fraction} onChange={setFraction} />
      <button
        type="button"
        className="btn btn-primary"
        disabled={!valid || mut.isPending}
        onClick={() => {
          mut.mutate();
        }}
      >
        <i className="ti ti-device-floppy text-base" />
        Save sizing
      </button>
    </div>
  );
}

function LabeledNumber({
  label,
  value,
  onChange,
}: Readonly<{ label: string; value: string; onChange: (v: string) => void }>): ReactElement {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <input
        type="number"
        className="input"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
    </label>
  );
}

interface TakeEntryProps {
  strategy: Strategy;
  disabled: boolean;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}

function useTakeEntryMutation(props: TakeEntryProps, done: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => thetaApi.takeEntry(props.strategy.id),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: STATUS_KEY(props.strategy.id) });
      props.onDone(res.detail);
      done();
    },
    onError: (err) => {
      const reason = err instanceof ApiError ? `HTTP ${String(err.status)}` : "network";
      props.onError(`Take entry failed: ${reason}`);
      done();
    },
  });
}

function TakeEntryRow(props: Readonly<TakeEntryProps>): ReactElement {
  const { disabled } = props;
  const [confirming, setConfirming] = useState(false);
  const mut = useTakeEntryMutation(props, () => {
    setConfirming(false);
  });
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="btn btn-primary"
        disabled={disabled}
        onClick={() => {
          setConfirming(true);
        }}
      >
        <i className="ti ti-player-play text-base" />
        Take entry
      </button>
      <span className="text-xs text-ink-muted">
        Direction follows the trend: spot above SuperTrend sells a PUT spread, below sells a CALL
        spread.
      </span>
      <TakeEntryDialog
        open={confirming}
        busy={mut.isPending}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          mut.mutate();
        }}
      />
    </div>
  );
}

function TakeEntryDialog({
  open,
  busy,
  onCancel,
  onConfirm,
}: Readonly<{
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}>): ReactElement {
  return (
    <ConfirmDialog
      open={open}
      title="Take a theta entry now?"
      description={
        "The engine picks the direction from the trend and sells the ~40-delta strike hedged " +
        "at ~25-delta, sized from your allocated funds. In dry-run it only Telegrams the " +
        "resolved basket."
      }
      confirmLabel="Take entry"
      icon="ti-player-play"
      tone="warning"
      busy={busy}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}
